/**
 * Формат чисел и дат (docs/04-finance.md, docs/11-nonfunctional.md).
 *
 * Десятичная запятая, разряды через неразрывный пробел, рубль после числа,
 * доллар перед числом, проценты без пробела. Отображение в Europe/Moscow,
 * хранение в UTC. Минус — типографский, а не дефис.
 */

const NBSP = ' ';
const MINUS = '−';
const MOSCOW = 'Europe/Moscow';

/** Intl ставит то узкий, то обычный пробел. Приводим к неразрывному. */
function normalizeSpaces(value: string): string {
  return value.replace(/[   ]/g, NBSP);
}

export function formatNumber(value: number, fractionDigits = 0): string {
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Math.abs(value));
  return (value < 0 ? MINUS : '') + normalizeSpaces(formatted);
}

const CURRENCY_SYMBOL: Record<string, string> = { USD: '$', RUB: '₽', EUR: '€' };

/**
 * Сумма из минорных единиц. Доллар и евро — символ перед числом,
 * рубль — после. Копейки у рубля показываются, только если они есть.
 */
export function formatMoney(minorUnits: number, currency = 'USD'): string {
  const negative = minorUnits < 0;
  const absolute = Math.abs(minorUnits) / 100;
  const symbol = CURRENCY_SYMBOL[currency] ?? currency;

  if (currency === 'RUB') {
    const digits = Math.abs(minorUnits) % 100 === 0 ? 0 : 2;
    return `${negative ? MINUS : ''}${normalizeSpaces(
      new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(absolute),
    )}${NBSP}${symbol}`;
  }

  const body = normalizeSpaces(
    new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      absolute,
    ),
  );
  return `${negative ? MINUS : ''}${symbol}${body}`;
}

export function formatPercent(value: number, fractionDigits = 1): string {
  return `${formatNumber(value, fractionDigits)}%`;
}

/** Разница со знаком: рост и падение видно без цвета. */
export function formatSignedPercent(value: number, fractionDigits = 1): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatNumber(value, fractionDigits)}%`;
}

/** Пункты процента: разница двух процентных величин. */
export function formatPercentPoints(value: number, fractionDigits = 1): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatNumber(value, fractionDigits)}${NBSP}п.п.`;
}

// ------------------------------------------------------------------- даты

function dateFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('ru-RU', { timeZone: MOSCOW, ...options });
}

export function formatDate(value: string | Date): string {
  // Intl добавляет «г.» после года, в текстах дизайн-системы его нет.
  return dateFormatter({ day: 'numeric', month: 'long', year: 'numeric' })
    .format(new Date(value))
    .replace(/\s*г\.$/, '');
}

export function formatDateShort(value: string | Date): string {
  return dateFormatter({ day: 'numeric', month: 'long' }).format(new Date(value));
}

export function formatTime(value: string | Date): string {
  return dateFormatter({ hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

export function formatDateTime(value: string | Date): string {
  return `${formatDateShort(value)}, ${formatTime(value)}`;
}

/** Русское склонение: 1 минута, 2 минуты, 5 минут. */
export function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** «Обновлено 12 минут назад». Возраст данных всегда виден (docs/10-design.md). */
export function formatRelative(value: string | Date, now: Date = new Date()): string {
  const then = new Date(value);
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (seconds < 0) return formatDateTime(then);
  if (seconds < 45) return 'только что';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const unit = plural(minutes, 'минуту', 'минуты', 'минут');
    return `${minutes} ${unit} назад`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const unit = plural(hours, 'час', 'часа', 'часов');
    return `${hours} ${unit} назад`;
  }

  const days = Math.floor(hours / 24);
  if (days === 1) return `вчера в ${formatTime(then)}`;
  if (days < 7) {
    const unit = plural(days, 'день', 'дня', 'дней');
    return `${days} ${unit} назад`;
  }

  return formatDateTime(then);
}
